import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { SensitiveInput } from "../../../../../components/ui/SensitiveInput";
import { useConnectOptro } from "../../../../../hooks/useOptroConnection";

interface OptroConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

// "Configure Optro" dialog: collects connection details and creates the
// workspace's Optro integration via the integrations endpoint.
export const OptroConnectDialog = ({
  open,
  onOpenChange,
  onConnected,
}: OptroConnectDialogProps) => {
  const [baseUrl, setBaseUrl] = useState("");
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const { connect, connecting } = useConnectOptro();

  const canConnect =
    baseUrl.trim() !== "" &&
    defaultWorkspaceId.trim() !== "" &&
    apiKey.trim() !== "" &&
    !connecting;

  const onConnect = async () => {
    const ok = await connect({ baseUrl, defaultWorkspaceId, apiKey });
    if (ok) {
      onConnected();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Configure Optro</DialogTitle>
          <DialogDescription>
            Enter your connection details to complete the setup.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="optro-base-url">Base URL</Label>
            <Input
              id="optro-base-url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://yourcompany.optro.ai"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="optro-workspace-id">Default Workspace ID</Label>
            <Input
              id="optro-workspace-id"
              value={defaultWorkspaceId}
              onChange={(e) => setDefaultWorkspaceId(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="optro-api-key">API Key</Label>
            <SensitiveInput
              id="optro-api-key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter API key"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canConnect} onClick={onConnect}>
            {connecting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
